/**
 * Barra de tempo em UMA linha: controles do loop, slider com a data/hora do
 * quadro (UTC e Brasília), relógio e um botão que abre as demais opções da
 * animação (passo, velocidade, modo, pausa) num diálogo. O progresso do
 * download é uma linha fina sob o slider. Só os pedaços que dependem do quadro
 * atual se re-renderizam durante a animação.
 *
 * O período continua visível no desktop (é a escolha mais frequente); a
 * estimativa de download e o aviso "só cabem N de M quadros" continuam na
 * barra nas duas plataformas (correção da tela preta do celular).
 *
 * À direita do eixo das imagens fica a zona da previsão do rastreio
 * (`tracking/ZonaDaPrevisao`): o futuro como continuação da linha do tempo. O
 * ▶ principal toca só o observado; com o cursor na zona o relógio passa a
 * dizer, em ciano, a hora em que a previsão vale.
 */
import clsx from 'clsx';
import {
  AlertTriangle,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  History,
  LoaderCircle,
  Lock,
  Pause,
  Play,
  Settings2,
  SkipBack,
  SkipForward,
} from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { formatBytes, formatInt } from '../lib/format';
import { formatDateBrt, formatDayTimeBrt, formatRelative, formatShortDateBrt, formatTimeBrt, formatUtcBesideBrt, MINUTE_MS } from '../lib/time';
import { ATRASO_NOAA_MS, mensagemAtrasoDaNoaa } from '../loop/autoRefresh';
import { usePlanStore } from '../loop/plan';
import { useDetalheStore } from '../detalhe/estado';
import { goToFirst, goToLast, stepFrame, togglePlay, useExportLock } from '../loop/playback';
import { loopLoadAllowed, useLoopStore } from '../loop/store';
import { useFireStore } from '../fires/store';
import { shownTimeOf, useTimeStore } from '../state/time';
import { useUiStore } from '../state/ui';
import { naZonaOuNoFluxo, teclaDaPrevisao } from '../tracking/fluxo';
import { useTrackingStore } from '../tracking/store';
import { rotuloAntecedencia } from '../tracking/tempo';
import { BotaoDaPrevisao } from '../tracking/BotaoDaPrevisao';
import { BarraDaPrevisao } from '../previsao/BarraDaPrevisao';
import { BotaoProximosDias } from '../previsao/BotaoProximosDias';
import { usePrevisaoStore } from '../previsao/store';
import { useCursorNaZona, usePrevisaoDesenhada, ZonaDaPrevisao } from '../tracking/ZonaDaPrevisao';
import { useLatestTime, useNow, useSemBaseNoQuadro } from './hooks';
import { useIdadeDaImagem } from './LatestStatus';
import { CustomPeriodButton, CustomPeriodDialog, LoopSettingsDialog, PeriodSelect, STEP_LABELS_SHORT } from './LoopSettings';
import { IconButton } from './primitives';
import { ResolutionChip, useResolutionLabel } from './ResolutionChip';
import { TimeAxis } from './TimeAxis';

/** Só mostra "carregando" se a espera pelo próximo quadro durar mais que isto (evita o ícone piscar a cada quadro). */
const BUFFERING_SHOW_MS = 400;

/** `buffering` atrasado: vira true só depois de `BUFFERING_SHOW_MS` contínuos; volta a false na hora. */
function useVisibleBuffering(): boolean {
  const buffering = useLoopStore((s) => s.buffering);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!buffering) {
      setVisible(false);
      return;
    }
    const id = setTimeout(() => setVisible(true), BUFFERING_SHOW_MS);
    return () => clearTimeout(id);
  }, [buffering]);
  return buffering && visible;
}

export function PlayButton(props: { size?: 'md' | 'lg' }) {
  const playing = useLoopStore((s) => s.playing);
  const buffering = useVisibleBuffering();
  const count = useTimeStore((s) => s.frames.length);
  const armed = useLoopStore((s) => s.loopArmed);
  const Icon = playing ? Pause : Play;
  return (
    <button
      type="button"
      onClick={togglePlay}
      disabled={count < 2}
      aria-label={playing ? 'Pausar animação (espaço)' : 'Reproduzir animação (espaço)'}
      title={
        buffering
          ? 'Esperando o próximo quadro chegar'
          : playing
            ? 'Pausar (espaço)'
            : armed
              ? 'Reproduzir (espaço)'
              : 'Reproduzir (espaço) — baixa as imagens do período e toca a animação'
      }
      className={clsx(
        'relative inline-flex shrink-0 items-center justify-center rounded-full bg-mata-500 text-ink-950 shadow transition-colors hover:bg-mata-400',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-mata-300 disabled:opacity-40',
        props.size === 'lg' ? 'size-12' : 'size-10',
      )}
    >
      {buffering ? (
        <LoaderCircle className="size-5 animate-spin" aria-hidden />
      ) : (
        <Icon className={clsx('size-5', !playing && 'translate-x-px')} fill="currentColor" aria-hidden />
      )}
    </button>
  );
}

/**
 * ‹ e › da barra (celular e desktop), como as setas do teclado: com a previsão na tela, andam
 * pelas antecedências (+30 → +1 h …; antes da primeira, a imagem anterior) e o fluxo que tocava
 * PARA — quem manda passa a ser o usuário (pedido de 08/10/2026). Fora dela, uma imagem.
 */
function passoNoTempo(delta: -1 | 1): void {
  if (teclaDaPrevisao(delta > 0 ? 'ArrowRight' : 'ArrowLeft', { focar: false })) return;
  const st = useTrackingStore.getState();
  if (st.fluxo) st.pararFluxo();
  stepFrame(delta);
}

/**
 * Celular: ‹ e › grandes, um em cada ponta da barra, para andar no tempo com os dois polegares —
 * como num controle de jogo (pedido do usuário, 08/10/2026).
 */
function PassoGrande(props: { lado: 'voltar' | 'avancar'; disabled: boolean }) {
  const voltar = props.lado === 'voltar';
  const Icone = voltar ? ChevronLeft : ChevronRight;
  return (
    <button
      type="button"
      onClick={() => passoNoTempo(voltar ? -1 : 1)}
      disabled={props.disabled}
      aria-label={voltar ? 'Quadro anterior (←)' : 'Próximo quadro (→)'}
      className={clsx(
        'inline-flex size-12 shrink-0 items-center justify-center rounded-2xl border border-ink-600 bg-ink-800 text-white shadow-sm transition-colors active:bg-ink-600',
        'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-mata-400 disabled:opacity-40',
      )}
    >
      <Icone className="size-7" aria-hidden />
    </button>
  );
}

function Transport(props: { compact?: boolean }) {
  const count = useTimeStore((s) => s.frames.length);
  const disabled = count < 2;
  if (props.compact) {
    // Celular: só o ‹ (o › vem do `PassoGrande` do outro lado). O play flutua sobre o mapa, no
    // meio da linha do pé (ui/RelogioDoPe); início e fim ficam no diálogo de opções.
    return <PassoGrande lado="voltar" disabled={disabled} />;
  }
  return (
    <div className="flex items-center gap-0.5">
      {/* Home e End saem da previsão como as teclas (ver `teclaDaPrevisao`); ‹ › andam pelas imagens. */}
      <IconButton icon={SkipBack} size="sm" label="Primeiro quadro (Home)" disabled={disabled} onClick={() => teclaDaPrevisao('Home', { focar: false }) || goToFirst()} />
      <IconButton icon={ChevronLeft} size="sm" label="Quadro anterior (←)" disabled={disabled} onClick={() => passoNoTempo(-1)} />
      <PlayButton />
      <IconButton icon={ChevronRight} size="sm" label="Próximo quadro (→)" disabled={disabled} onClick={() => passoNoTempo(1)} />
      <IconButton icon={SkipForward} size="sm" label="Último quadro (End)" disabled={disabled} onClick={() => teclaDaPrevisao('End', { focar: false }) || goToLast()} />
    </div>
  );
}

/** Progresso do download como linha fina (2 px) sob o slider; some quando termina. */
function LoadProgressLine() {
  const progress = usePlanStore((s) => s.progress);
  // Antes do primeiro play só o quadro na tela baixa: contar o loop inteiro
  // ("0 de 18") pareceria download travado. A linha mostra só o quadro atual.
  const loopAll = useLoopStore((s) => loopLoadAllowed(s));
  const current = useCurrentFrameLoading();
  const { downloaded, failed, total } = loopAll ? progress : { downloaded: current ? 0 : 1, failed: 0, total: 1 };
  // Blocos do detalhe (área visível em alta resolução) contam na mesma linha.
  const blocos = useDetalheStore((s) => s.progresso);
  const all = total + blocos.total;
  if (all === 0) return null;
  const done = downloaded + failed + blocos.baixados;
  if (done >= all) return null;
  const detalhe = blocos.total > blocos.baixados ? ` e ${formatInt(blocos.baixados)} de ${formatInt(blocos.total)} blocos de detalhe` : '';
  return (
    <div
      className="absolute inset-x-2 top-0 h-0.5 overflow-hidden rounded-full bg-ink-700/70"
      role="progressbar"
      aria-label="Baixando as imagens da animação"
      aria-valuemin={0}
      aria-valuemax={all}
      aria-valuenow={done}
      title={
        loopAll
          ? `Baixando ${formatInt(downloaded)} de ${formatInt(total)} imagens${detalhe}. A animação já pode tocar enquanto baixa.`
          : `Baixando a imagem deste horário${detalhe}. As demais imagens do período baixam ao tocar a animação.`
      }
    >
      <div className="h-full rounded-full bg-mata-400/80 transition-[width] duration-200" style={{ width: `${(done / all) * 100}%` }} />
    </div>
  );
}

/** Alguma camada visível ainda carregando a imagem do quadro atual. */
function useCurrentFrameLoading(): boolean {
  return usePlanStore((s) => Object.values(s.frameStatus).includes('loading'));
}

/**
 * Aviso de que o loop ainda não foi baixado: o portal abre parado na medição
 * mais recente e só baixa o resto do período quando o usuário aperta play.
 * Some depois do primeiro play.
 */
function PlayToLoadHint() {
  const loopAll = useLoopStore((s) => loopLoadAllowed(s));
  const count = useTimeStore((s) => s.frames.length);
  const mb = usePlanStore((s) => s.plan?.bytesTotal ?? 0);
  if (loopAll || count < 2) return null;
  return (
    <span
      className="min-w-0 shrink-[4] truncate font-sans text-ink-300"
      title={`Aperte ▶ para baixar as ${formatInt(count)} imagens do período (≈ ${formatBytes(mb)}) e tocar a animação.`}
    >
      {/* Curto: divide o centro com "mais recente" e encolhe primeiro; o número de imagens e o tamanho ficam na dica. */}
      ▶ o loop baixa ao tocar
    </span>
  );
}

/**
 * Um toque no eixo das imagens com o cursor na zona (ou o fluxo dela tocando) NÃO desliga a
 * previsão: ela segue a partir da imagem tocada — a previsão feita naquela hora. Só o fluxo para.
 */
function aoBuscarObservado(): void {
  if (naZonaOuNoFluxo() && useTrackingStore.getState().fluxo) useTrackingStore.getState().setFluxo(false);
}

const teclaAntes = (tecla: string) => teclaDaPrevisao(tecla);

function FrameSlider(props: { compact?: boolean }) {
  const naZona = useCursorNaZona();
  const count = useTimeStore((s) => s.frames.length);
  const index = useTimeStore((s) => s.index);
  const currentTime = useTimeStore((s) => s.currentTime);
  const latest = useLatestTime();
  const now = useNow();
  // Alguma camada visível sem imagem neste horário (falha no download).
  const failed = usePlanStore((s) => Object.values(s.frameStatus).includes('failed'));
  const custom = useLoopStore((s) => s.period === 'custom');
  const isLatest = currentTime !== null && currentTime === latest && !custom;
  const first = useTimeStore((s) => s.frames[0] ?? null);
  const last = useTimeStore((s) => s.frames.at(-1) ?? null);
  // Período que cruza a meia-noite (Brasília): mostra o dia nos rótulos.
  const multiDay = first !== null && last !== null && formatDateBrt(first) !== formatDateBrt(last);
  // O último quadro é a imagem mais recente que a NOAA publicou?
  const lastIsLatest = last !== null && latest !== null && last === latest && !custom;
  // A NOAA parou de publicar (ver `ATRASO_NOAA_MS`): o rótulo do centro diz isso, e não só "há 3 h".
  const noaaAtrasada = latest !== null && now - latest > ATRASO_NOAA_MS;
  const label = (t: number) => (multiDay ? formatDayTimeBrt(t) : formatTimeBrt(t));
  const semBase = useSemBaseNoQuadro();
  const pendingFrom = usePendingFiresIndex();
  const currentPending = pendingFrom >= 0 && index >= pendingFrom;
  const labels = (
    <div
      className={clsx(
        '@container flex h-4 items-center justify-between gap-2 overflow-hidden font-mono text-[10px] leading-4 whitespace-nowrap text-ink-300 tabular-nums',
        !props.compact && 'mt-0.5',
      )}
    >
      {/*
        Linha de rótulos com altura FIXA (uma linha, sem quebra): os avisos do
        quadro ("mais recente", "imagem indisponível", "sem focos ainda") ficam
        aqui, no centro, e nunca mudam a altura da barra nem o tamanho do mapa.
        No celular o centro mostra a estimativa de download (e o aviso de
        truncagem); o relógio ao lado já mostra o quadro e o fuso.
      */}
      {/* Celular com a zona da previsão ao lado: o eixo encolhe, e o início do período é o primeiro a sair — a resolução e o fim ficam. */}
      <span className={clsx('shrink-0', props.compact ? '@max-[170px]:hidden' : '@max-[72px]:hidden')}>{first ? label(first) : '--:--'}</span>
      {props.compact ? (
        <Estimate compact />
      ) : (
        <span className="flex min-w-0 items-center gap-2 overflow-hidden">
          <span className="shrink-0">{count ? `${index + 1}/${count}` : ''}</span>
          {/* O aviso do próprio quadro tem prioridade; "sem focos" encolhe primeiro. */}
          {failed ? (
            <span className="min-w-0 truncate font-sans text-yellow-300" title="Imagem indisponível neste horário">
              imagem indisponível neste horário
            </span>
          ) : semBase ? (
            // A linha do tempo segue sem a base (loop/semNoaa.ts): as outras camadas estão neste horário.
            <span
              // Encolhe bem depois do "o loop baixa ao tocar": a data é o que importa aqui.
              className="min-w-0 shrink-0 truncate font-sans text-yellow-200"
              title={
                semBase.noFim
                  ? mensagemAtrasoDaNoaa(`${formatDayTimeBrt(semBase.desde)} (Brasília)`, semBase.produto)
                  : `A NOAA não publicou “${semBase.produto}” entre ${formatDayTimeBrt(semBase.desde)} e ${formatDayTimeBrt(semBase.ate)} (Brasília). As outras camadas seguem no horário.`
              }
            >
              {/* Curto: dividia o centro com "o loop baixa ao tocar" e perdia justo a data. */}
              {semBase.noFim ? `▲ parada desde ${formatDayTimeBrt(semBase.desde)}` : '▲ sem imagem da NOAA neste horário'}
            </span>
          ) : (
            isLatest &&
            (noaaAtrasada ? (
              // A NOAA parou de publicar: dizer só "mais recente · há 3 h" em verde parecia o AmaView
              // travado (20/10/2026, pane do STAR das 15:16 UTC em diante). O aviso de 15 s já tinha sumido.
              <span
                className="min-w-0 truncate font-sans text-yellow-200"
                title={mensagemAtrasoDaNoaa(`${formatTimeBrt(currentTime)} (Brasília)`)}
              >
                ▲ NOAA atrasada · {formatRelative(currentTime, now)}
              </span>
            ) : (
              <span className="min-w-0 truncate font-sans text-mata-300" title="Esta é a imagem mais recente publicada pela NOAA">
                ● mais recente · {formatRelative(currentTime, now)}
              </span>
            ))
          )}
          <PlayToLoadHint />
          {currentPending && (
            <span className="min-w-0 shrink-[3] truncate font-sans text-yellow-200/90" title={PENDING_FIRES_HINT}>
              ▮ sem focos ainda
            </span>
          )}
        </span>
      )}
      {/*
        O fim da barra é a imagem mais recente publicada: o ponto verde marca
        isso SEMPRE (não só com o loop parado nela). No celular, com a zona da
        previsão ao lado, o eixo fica com ~90 px: aí ele sai e fica a resolução
        (o relógio ao lado mostra a hora e o ponto verde da mais recente).
      */}
      <span
        // Celular estreito com a zona da previsão: o relógio ao lado já mostra a hora e o ponto verde.
        className={clsx('flex shrink-0 items-center gap-1', props.compact && '@max-[110px]:hidden')}
        title={
          !lastIsLatest
            ? undefined
            : noaaAtrasada
              ? mensagemAtrasoDaNoaa(`${formatTimeBrt(latest)} (Brasília)`)
              : `Imagem mais recente publicada pela NOAA (${formatRelative(latest, now)})`
        }
      >
        {/* Com a NOAA parada o ponto fica amarelo, como o "▲" do centro e o relógio do celular. */}
        {lastIsLatest && (
          <span className={clsx('size-1.5 rounded-full', noaaAtrasada ? 'bg-yellow-300' : 'bg-mata-400')} role="img" aria-label="Imagem mais recente" />
        )}
        {last ? label(last) : '--:--'}
      </span>
    </div>
  );
  return (
    // No desktop o eixo tem piso de 260 px: era o único elástico da barra e
    // encolhia até sumir quando os painéis deixavam pouca largura.
    <div className={clsx('flex-1', props.compact ? 'min-w-0' : 'min-w-[260px]')}>
      {/* A zona da previsão é uma coluna ao lado, com a mesma estrutura: eixo, linha do download, rótulos. */}
      <div className={clsx('flex items-start', props.compact ? 'gap-1' : 'gap-1.5')}>
        <div className="min-w-0 flex-1">
          {/* Eixo proporcional ao tempo (marcas de hora, buracos, "agora", faixa sem focos). No celular os rótulos ficam dentro do alvo de 44 px. */}
          <TimeAxis
            compact={props.compact}
            pendingFrom={pendingFrom}
            footer={props.compact ? labels : undefined}
            cursorFora={naZona}
            aoBuscarObservado={aoBuscarObservado}
            teclaAntes={teclaAntes}
          />
          {/* Linha fina: progresso do download. */}
          <div className="relative h-1">
            <LoadProgressLine />
          </div>
          {!props.compact && labels}
        </div>
        <ZonaDaPrevisao compact={props.compact} />
      </div>
    </div>
  );
}

const PENDING_FIRES_HINT = 'Focos do INPE ainda não publicados para estes horários (chegam cerca de 20 min depois das imagens).';

/**
 * Índice do primeiro quadro mais novo que o último foco do GOES-19 publicado
 * pelo INPE (−1 se nenhum, ou se os focos de 10 min estão desligados).
 */
function usePendingFiresIndex(): number {
  // Até onde o INPE já publicou (arquivo vazio conta: hora sem focos), e não o último foco.
  const latestFire = useFireStore((s) => (s.sources.goes10min ? (s.status.goes10min.publicadoAte ?? s.status.goes10min.latest) : null));
  const frames = useTimeStore((s) => s.frames);
  if (latestFire === null || frames.length === 0) return -1;
  return frames.findIndex((t) => t > latestFire);
}

/**
 * Relógio do quadro. Altura FIXA (2 linhas no desktop, 3 no celular): os avisos
 * variáveis ficam na linha de rótulos do slider, para a barra nunca mudar de
 * tamanho durante a animação. No celular, um ponto verde marca o mais recente.
 *
 * Com contorno previsto no mapa o relógio diz a hora em que a previsão VALE,
 * em ciano (a cor do tracejado), e embaixo de que imagem ela parte — as mesmas
 * duas linhas, a mesma altura.
 */
function FrameClock(props: {
  compact?: boolean;
  /**
   * Chip do topo do celular: na largura do conteúdo, e a linha de baixo diz a idade da imagem mais
   * recente ("há 19 min · Brasília") ou o aviso (sem conexão, NOAA atrasada), no lugar do status.
   */
  comIdade?: boolean;
  /**
   * Relógio do pé do celular: o período da animação ("3 h") na primeira linha, à direita da hora,
   * no espaço que sobra nela (a de baixo, "há 19 min · Brasília", já ocupa a largura toda): o
   * relógio não cresce, e o toque nele abre o menu das últimas horas (ui/RelogioDoPe).
   */
  periodo?: string;
}) {
  const t = useTimeStore((s) => s.currentTime);
  const mostrada = useTimeStore((s) => shownTimeOf(s));
  const prevista = usePrevisaoDesenhada();
  const latest = useLatestTime();
  // Alguma camada visível sem imagem neste horário (falha no download).
  const failed = usePlanStore((s) => Object.values(s.frameStatus).includes('failed'));
  const custom = useLoopStore((s) => s.period === 'custom');
  // Desktop: a largura do conteúdo (algarismos tabulares: não pula de um quadro para o outro). Os
  // 150 px fixos deixavam um vão entre o botão da previsão e a hora.
  const { idade, aviso } = useIdadeDaImagem();
  const box = clsx(
    'shrink-0 leading-tight',
    // Celular: largura FIXA, a do texto mais longo ("há 59 min · Brasília", ~100 px).
    // Na largura do conteúdo, cada minuto ("há 9 min" → "há 10 min") empurrava o "Pesquisar" ao lado.
    // Celular: as duas linhas centradas na altura do chip (com `leading-tight` a de baixo encostava na borda).
    props.comIdade
      ? 'flex h-[38px] w-[104px] flex-col justify-center text-left [&>*:first-child]:leading-5 [&>*:last-child]:leading-[14px]'
      : props.compact
        ? 'h-[38px] w-[82px] text-right'
        : 'h-[40px] min-w-[88px] text-right',
  );
  if (t === null) {
    return <div className={clsx(box, 'pt-1 text-xs text-ink-400')}>Carregando…</div>;
  }
  const isLatest = t === latest && !custom && !failed;
  const periodo = props.comIdade && props.periodo ? <PeriodoNoRelogio texto={props.periodo} /> : null;
  if (prevista > 0 && mostrada !== null) {
    return (
      <div className={box} aria-live="off" data-relogio-previsao>
        <div className={clsx('text-lg font-semibold text-previsao tabular-nums', periodo && 'flex items-center')}>
          {periodo && <span className="w-3 shrink-0" aria-hidden />}
          {formatTimeBrt(mostrada + prevista * MINUTE_MS)}
          {!props.compact && <span className="ml-1 text-[11px] font-normal text-ink-300">Brasília</span>}
          {periodo}
        </div>
        <div className={clsx('text-ink-200 tabular-nums', props.comIdade ? 'truncate text-[11px]' : 'truncate text-[10px]')}>
          {props.compact ? `prev. ${rotuloAntecedencia(prevista)}` : `previsão · imagem ${formatTimeBrt(mostrada)}`}
        </div>
      </div>
    );
  }
  if (props.comIdade) {
    // Celular: a bolinha (ou o aviso de falha) tem um lugar fixo à esquerda da hora, ocupado ou não:
    // sem ela a hora pulava 12 px para a esquerda a cada quadro fora do mais recente (pedido do
    // usuário, 29/10/2026).
    return (
      <div className={box} aria-live="off">
        <div className={clsx('flex items-center text-lg font-semibold tabular-nums', failed ? 'text-yellow-200' : 'text-white')}>
          <span className="flex w-3 shrink-0 items-center" data-lugar-da-bolinha="">
            {failed ? (
              <AlertTriangle className="size-3 text-yellow-300" aria-label="Imagem indisponível neste horário" />
            ) : (
              isLatest && (
                <span
                  className={clsx('inline-block size-2 rounded-full', aviso ? 'bg-yellow-300' : 'bg-mata-400')}
                  title="Imagem mais recente"
                  aria-label="Imagem mais recente"
                />
              )
            )}
          </span>
          {formatTimeBrt(t)}
          {periodo}
        </div>
        <div className="truncate text-[11px] text-ink-200 tabular-nums">
          {aviso ? <span className="text-yellow-200">{aviso}</span> : isLatest && idade ? `${idade} · Brasília` : `${formatShortDateBrt(t)} · Brasília`}
        </div>
      </div>
    );
  }
  return (
    <div className={box} aria-live="off">
      <div className={clsx('text-lg font-semibold tabular-nums', failed ? 'text-yellow-200' : 'text-white')}>
        {failed && <AlertTriangle className="mr-1 inline size-3.5 -translate-y-px text-yellow-300" aria-label="Imagem indisponível neste horário" />}
        {props.compact && isLatest && (
          <span
            className="mr-1 inline-block size-2 -translate-y-0.5 rounded-full bg-mata-400"
            title="Imagem mais recente"
            aria-label="Imagem mais recente"
          />
        )}
        {formatTimeBrt(t)}
        {!props.compact && <span className="ml-1 text-[11px] font-normal text-ink-300">Brasília</span>}
      </div>
      {/* Desktop: só a data, mais curta; o UTC vai para a dica. */}
      <div
        className="truncate text-[10px] text-ink-300 tabular-nums"
        title={props.compact ? undefined : `${formatShortDateBrt(t)} · ${formatUtcBesideBrt(t)}`}
      >
        {props.compact ? `${formatShortDateBrt(t)} · Brasília` : formatShortDateBrt(t)}
      </div>
    </div>
  );
}

/** O período ("3 h") no canto da primeira linha do relógio do celular, com o símbolo das últimas horas. */
function PeriodoNoRelogio(props: { texto: string }) {
  return (
    <span className="ml-auto inline-flex items-center gap-0.5 text-[11px] font-semibold text-ink-200" data-periodo-no-relogio="">
      <History className="size-3 text-mata-400" aria-hidden />
      {props.texto}
    </span>
  );
}

/**
 * Aviso de plano truncado e, no celular, a resolução na tela ("1,8k auto").
 * No desktop o tamanho do download fica dentro do botão de resolução.
 */
function Estimate(props: { compact?: boolean }) {
  // Seletores de valores primitivos: um objeto novo a cada leitura faria o
  // React entrar em laço infinito ("getSnapshot should be cached") sempre que
  // o plano fosse truncado — tela preta no celular com Máxima ou 48 h.
  const capacity = usePlanStore((s) => (s.plan?.truncated ? s.plan.capacity : null));
  const requested = usePlanStore((s) => (s.plan?.truncated ? s.plan.requested : null));
  const resolution = useResolutionLabel();
  const cut =
    capacity !== null && requested !== null
      ? `Só cabem ${formatInt(capacity)} de ${formatInt(requested)} quadros nesta qualidade; mostrando os mais recentes.`
      : null;
  if (!props.compact && !cut) return null;
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1 overflow-hidden whitespace-nowrap text-ink-200 tabular-nums',
        props.compact ? 'min-w-0 shrink font-sans text-[10px] [&>span]:truncate' : 'shrink-0 text-[11px]',
      )}
      title={cut ?? 'Resolução das imagens na tela (troque em Opções da animação)'}
    >
      {/* Em 360 px, com a zona da previsão ao lado, o centro fica estreito: a
          resolução some inteira em vez de virar "1." (continua em Opções da animação). */}
      {props.compact && <span className="@max-[60px]:hidden">{resolution ?? ''}</span>}
      {cut && <AlertTriangle className="size-3.5 shrink-0 text-yellow-300" aria-label={cut} />}
    </span>
  );
}

/** Resumo das opções (passo · velocidade · modo) que abre o diálogo no desktop. */
function LoopOptionsButton() {
  const step = useLoopStore((s) => s.step);
  const fps = useLoopStore((s) => s.fps);
  const mode = useLoopStore((s) => s.mode);
  const set = useUiStore((s) => s.set);
  return (
    <button
      type="button"
      onClick={() => set({ loopOptionsOpen: true })}
      aria-label="Opções da animação"
      title={`Intervalo entre quadros, velocidade, modo (${mode === 'loop' ? 'repetir' : 'vai e volta'}) e pausa no fim`}
      className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg border border-ink-600 bg-ink-800 px-2.5 text-xs text-ink-100 hover:bg-ink-700 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-mata-400"
    >
      <Settings2 className="size-3.5 text-ink-400" aria-hidden />
      {/* Em barra estreita fica só a engrenagem: o mesmo botão, sem o resumo. */}
      <span className="font-mono text-[11px] tabular-nums @max-[1110px]:hidden">
        {STEP_LABELS_SHORT[step]} · {fps} q/s
      </span>
    </button>
  );
}

/**
 * Celular: o relógio é o botão que abre as opções (período, passo, velocidade…). Fica no chip do
 * topo, ao lado do logo (`ui/MapActions` `BrandChip`), e não mais na barra (08/10/2026).
 */
export function ClockButton(props: {
  comIdade?: boolean;
  /**
   * Relógio do pé do celular (ui/RelogioDoPe): sem a seta, e o toque abre o menu das últimas horas
   * (o mesmo do "3 h" ao lado, que tem "Mais opções…"): um controle só, não dois.
   */
  noPe?: { aberto: boolean; alternar: () => void; periodo: string };
}) {
  const set = useUiStore((s) => s.set);
  // Fora da barra (no chip do topo), a trava da exportação não vem do <fieldset>.
  const travado = useExportLock();
  if (props.noPe) {
    return (
      <button
        type="button"
        disabled={travado}
        onClick={props.noPe.alternar}
        aria-expanded={props.noPe.aberto}
        aria-haspopup="true"
        aria-label={`Horário da imagem; animação das últimas ${props.noPe.periodo}. Escolher as últimas horas`}
        data-periodo-rapido=""
        className="flex min-h-11 shrink-0 items-center rounded-lg px-1.5 hover:bg-ink-800 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-mata-400"
      >
        <FrameClock compact comIdade={props.comIdade} periodo={props.noPe.periodo} />
      </button>
    );
  }
  return (
    <button
      type="button"
      disabled={travado}
      onClick={() => set({ loopOptionsOpen: true })}
      aria-label="Opções da animação"
      title="Período, intervalo, velocidade, modo e pausa no fim"
      className="flex min-h-11 shrink-0 items-center gap-0.5 rounded-lg pr-1.5 pl-1 hover:bg-ink-800 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-mata-400"
    >
      <FrameClock compact comIdade={props.comIdade} />
      <ChevronDown className="size-3.5 shrink-0 text-ink-300" aria-hidden />
    </button>
  );
}

/**
 * Durante a exportação (`setExportLock(true)`) todos os controles ficam
 * desativados: um `<fieldset disabled>` desliga botões, slider e selects.
 */
function LockableControls(props: { children: ReactNode }) {
  const locked = useExportLock();
  return (
    <fieldset disabled={locked} className={clsx('m-0 min-w-0 border-0 p-0', locked && 'opacity-60')}>
      {props.children}
    </fieldset>
  );
}

/**
 * Aviso da trava de exportação SOBREPOSTO à barra (posição absoluta): não
 * muda a altura da barra nem o tamanho do mapa durante a gravação.
 */
function ExportLockNote() {
  const locked = useExportLock();
  if (!locked) return null;
  return (
    <div
      className="absolute inset-0 z-10 flex items-center justify-center bg-ink-900/75 text-xs text-ink-100 backdrop-blur-[1px]"
      role="status"
    >
      <span className="flex items-center gap-1.5 rounded-full border border-ink-600 bg-ink-850 px-3 py-1.5 shadow">
        <Lock className="size-3.5" aria-hidden />
        Exportando a animação — os controles voltam ao terminar.
      </span>
    </div>
  );
}

export function TimeBar(props: { mobile: boolean }) {
  const optionsOpen = useUiStore((s) => s.loopOptionsOpen);
  const set = useUiStore((s) => s.set);
  const semQuadros = useTimeStore((s) => s.frames.length < 2);
  // "Próximos dias": a barra inteira vira a da previsão (um relógio só por vez).
  const modoPrevisao = usePrevisaoStore((s) => s.modo);
  if (modoPrevisao) return <BarraDaPrevisao mobile={props.mobile} />;

  if (props.mobile) {
    return (
      <section
        data-timebar
        // 8 px das bordas, como o pé do mapa (ui/RelogioDoPe) e a barra da previsão.
        className="relative border-t border-ink-700/80 bg-ink-900 px-2 pt-1.5 pb-[calc(env(safe-area-inset-bottom)+6px)]"
        aria-label="Linha do tempo"
      >
        <ExportLockNote />
        <LockableControls>
          <div className="flex items-center gap-2">
            <Transport compact />
            {/* Zona morta lateral: o deslize no slider não disputa com a navegação por borda do navegador. */}
            <div className="min-w-0 flex-1 px-1">
              <FrameSlider compact />
            </div>
            <PassoGrande lado="avancar" disabled={semQuadros} />
          </div>
        </LockableControls>
        <LoopSettingsDialog open={optionsOpen} onClose={() => set({ loopOptionsOpen: false })} mobile />
        <CustomPeriodDialog />
      </section>
    );
  }

  return (
    /*
      `@container`: o que manda é a largura da BARRA, não a da janela — com o
      painel e o inspetor abertos a barra fica bem menor que a tela. Faltando
      espaço, o eixo do tempo não pode ser espremido até sumir (ele era o único
      elástico entre controles todos rígidos): quem sai de cena, na ordem, é o
      que o diálogo de opções (⚙) já repete.
    */
    <section data-timebar className="@container relative border-t border-ink-700/80 bg-ink-900 px-3 py-2" aria-label="Linha do tempo">
      <ExportLockNote />
      <LockableControls>
        <div className="flex items-center gap-3">
          <Transport />
          <div className="flex shrink-0 items-center gap-1 @max-[1000px]:hidden">
            <PeriodSelect className="w-[136px] @max-[1030px]:w-[104px]" />
            <CustomPeriodButton />
          </div>
          <FrameSlider />
          {/* À direita do eixo: liga a previsão na última imagem rastreada; a dica diz que é extrapolação. */}
          <BotaoDaPrevisao />
          <FrameClock />
          {/* Entra no modo "Próximos dias" (a previsão do ECMWF e do CAMS). */}
          <BotaoProximosDias />
          <div className="flex shrink-0 items-center gap-1.5 @max-[830px]:hidden">
            <Estimate />
            <ResolutionChip />
          </div>
          <LoopOptionsButton />
        </div>
      </LockableControls>
      <LoopSettingsDialog open={optionsOpen} onClose={() => set({ loopOptionsOpen: false })} />
      <CustomPeriodDialog />
    </section>
  );
}
